/**
 * WebSocket connection with automatic reconnect. The session token lives in
 * localStorage so a refresh or a dropped connection resumes the same seat.
 */

import { type ClientMessage, type RulesConfig, type ServerMessage, normalizeRules } from '@euchre/core';

/** 'superseded': this session was opened in another tab or device, so this tab stops reconnecting. */
export type ConnStatus = 'connecting' | 'open' | 'reconnecting' | 'superseded';

const TOKEN_KEY = 'euchre.token';
const NAME_KEY = 'euchre.name';
// Saved house-rule choices. The key's version was bumped in 1.2.0 when every house rule became
// off by default, so choices saved before then are forgotten once and everyone starts fresh.
const RULES_KEY = 'euchre.rules.v2';
const OLD_RULES_KEYS = ['euchre.rules'];

export function storedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function storeName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* private mode */
  }
}

/** The house rules this browser last chose for a table it hosts (defaults if none). */
export function storedRules(): RulesConfig {
  try {
    for (const k of OLD_RULES_KEYS) localStorage.removeItem(k);
    const raw = localStorage.getItem(RULES_KEY);
    return normalizeRules(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeRules(null);
  }
}

export function storeRules(rules: RulesConfig): void {
  try {
    localStorage.setItem(RULES_KEY, JSON.stringify(rules));
  } catch {
    /* private mode */
  }
}

function storedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private mode: the session lasts as long as the tab */
  }
}

export class Net {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private queue: ClientMessage[] = [];
  private token: string | null = storedToken();
  private ready = false;
  private pingTimer: number | null = null;
  status: ConnStatus = 'connecting';

  constructor(
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onStatus: (s: ConnStatus) => void,
    private readonly name: () => string,
  ) {}

  connect(): void {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      ws.send(JSON.stringify({ type: 'hello', token: this.token ?? undefined, name: this.name() } satisfies ClientMessage));
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'welcome') {
        this.token = msg.token;
        storeToken(msg.token);
        this.ready = true;
        this.setStatus('open');
        const pending = this.queue.splice(0);
        for (const m of pending) ws.send(JSON.stringify(m));
      }
      this.onMessage(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ready = false;
      this.ws = null;
      // The server closes with 4001 when the same player connects from another tab or device.
      // Reconnecting here would take the seat back and the two tabs would fight over it forever,
      // so wait until the player chooses to play from this tab again.
      if (ev.code === 4001) {
        this.queue = [];
        this.setStatus('superseded');
        return;
      }
      this.setStatus('reconnecting');
      const delay = Math.min(8000, 400 * 2 ** this.attempts) + Math.random() * 400;
      this.attempts++;
      window.setTimeout(() => this.connect(), delay);
    };
    if (this.pingTimer === null) {
      // Keeps proxies from idling the connection out and detects dead links sooner.
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }, false), 20_000);
    }
  }

  /** Take the session back after another tab took it over. */
  resume(): void {
    if (this.status !== 'superseded') return;
    this.attempts = 0;
    this.setStatus('connecting');
    this.connect();
  }

  private setStatus(s: ConnStatus): void {
    this.status = s;
    this.onStatus(s);
  }

  /** Send now if connected; otherwise queue (unless `queue` is false). */
  send(msg: ClientMessage, queue = true): void {
    if (this.ws && this.ready && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else if (queue && msg.type !== 'game_action') {
      // Game actions are never queued: the table may have moved on by the time we reconnect.
      this.queue.push(msg);
    }
  }
}
